// URLs on the public web views are credentials. /m/:token doubles as a
// one-time sign-in, and /signin/:token, /invite/:token, /unsubscribe/:token and
// /password-reset/:token each carry a token that is the whole authorization. A
// token that reaches Datadog is a live credential sitting in a dashboard,
// readable by anyone with access to the org.
//
// So every URL leaving the browser goes through here first — see the beforeSend
// hook in entrypoints/rum.js.
//
// Two exported functions, because URLs reach Datadog in two shapes.
// `redactUrl` takes a field that is only a URL (view.url, resource.url).
// `redactUrlsInText` takes a field that may contain one anywhere inside it: the
// SDK writes network failures into `error.message` as
// "XHR error GET https://host/m/<token>", and a stack frame carries whatever
// URL it was thrown from. Scrubbing only the URL-shaped fields leaves that
// message holding a live credential.
//
// Both route through `redactPathname`, so the route list and the length
// backstop apply identically whether a URL arrives on its own or inside prose.
// A credential is a credential in either shape.
//
// Kept in its own module, free of any DOM reference, so redact_url.test.js can
// import it.

// The second path segment on these routes is the credential.
const TOKEN_ROUTES = ["m", "signin", "invite", "unsubscribe", "password-reset"]

// Backstop for opaque segments the list above doesn't name — Active Storage
// signed ids most of all, which grant access to a photo. Every token we mint is
// longer than this; nothing worth keeping is (`/moment/:id` is numeric, and
// stays legible).
const OPAQUE_SEGMENT_LENGTH = 20

function redactPathname(pathname) {
  const segments = pathname.split("/")

  return segments
    .map((segment, index) => {
      if (index === 2 && TOKEN_ROUTES.includes(segments[1])) return ":token"
      if (segment.length >= OPAQUE_SEGMENT_LENGTH) return ":token"
      return segment
    })
    .join("/")
}

export function redactUrl(raw, origin) {
  let url

  try {
    url = new URL(raw, origin)
  } catch {
    // Unparseable: drop it rather than forward something we couldn't inspect.
    return ""
  }

  // Query and fragment go whole. A magic link's `redirect_path` travels in the
  // query string, and no page on these surfaces has a parameter worth keeping.
  return `${url.origin}${redactPathname(url.pathname)}`
}

// An absolute URL embedded in free text. Stops at whitespace and at the
// punctuation that tends to close a URL in a message or a stack frame, so a
// trailing paren or quote isn't swallowed into the host.
const ABSOLUTE_URL = /https?:\/\/[^\s"'`<>)\]]+/

// A path with no origin in front of it. Two segments minimum so a lone slash or
// a bare word isn't rewritten, and the character class is URL path characters
// only, which keeps it off ordinary prose.
const BARE_PATH = /(?:\/[A-Za-z0-9._~%+-]+){2,}/

// One pass, with the absolute form first. Alternation is ordered, so a match
// starting at "https" consumes the whole URL and the bare-path branch never
// sees its interior. Two chained replaces would instead let the second pass
// read "//host/file" as a path and rewrite a host of 20 characters or more to
// ":token", destroying the hostname an error message exists to report.
const URL_OR_PATH = new RegExp(`${ABSOLUTE_URL.source}|${BARE_PATH.source}`, "g")

export function redactUrlsInText(text, origin) {
  if (typeof text !== "string") return text

  return text.replace(URL_OR_PATH, (match) =>
    /^https?:\/\//.test(match) ? redactUrl(match, origin) : redactPathname(match)
  )
}
